import { useCallback, useEffect, useRef, useState } from 'react'

import { idToken, initAuth, signIn } from './firebase'

type Gate =
  | { kind: 'checking' }
  | { kind: 'open' }
  | { kind: 'needs-signin'; error: string | null }
  | { kind: 'broken'; error: string }

/**
 * Decides whether this deployment wants anybody to sign in, by asking it.
 *
 * The backend already knows — `fluence.identity.mode` picks the resolver — so the frontend
 * does not carry a second copy of that decision. It calls `/api/me`: a 200 means the
 * request was accepted as it stands, which covers both DEV mode and an already-valid token,
 * and a 401 means a token is wanted. Nothing here is configured per environment, and
 * running locally never sees a sign-in screen because DEV mode never answers 401.
 *
 * The Firebase SDK is loaded only on that 401, so a local session never initialises it.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const [gate, setGate] = useState<Gate>({ kind: 'checking' })
  const started = useRef(false)

  const probe = useCallback(async (): Promise<boolean> => {
    const token = idToken()
    const response = await fetch('/api/me', {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
    return response.status === 200
  }, [])

  useEffect(() => {
    // React runs effects twice in development, and this one signs people in.
    if (started.current) return
    started.current = true

    void (async () => {
      try {
        if (await probe()) {
          setGate({ kind: 'open' })
          return
        }
        // A 401 means this deployment authenticates. Bring up the SDK and let it restore a
        // session if there is one, which is the common case on a revisit.
        await initAuth()
        setGate((await probe()) ? { kind: 'open' } : { kind: 'needs-signin', error: null })
      } catch (e) {
        setGate({ kind: 'broken', error: e instanceof Error ? e.message : String(e) })
      }
    })()
  }, [probe])

  const onSignIn = async () => {
    try {
      await signIn()
      setGate((await probe()) ? { kind: 'open' } : {
        kind: 'needs-signin',
        // Signed in to Google, refused by Fluence: the token is valid and simply is not for
        // this service, which is a deployment mismatch rather than anything the user did.
        error: 'That account signed in, but this plan would not accept it.',
      })
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      setGate({
        kind: 'needs-signin',
        error: message.includes('popup-closed-by-user') ? null : message,
      })
    }
  }

  if (gate.kind === 'open') return <>{children}</>

  if (gate.kind === 'checking') {
    return <div className="auth-screen">Checking your session…</div>
  }

  if (gate.kind === 'broken') {
    return (
      <div className="auth-screen">
        <h1>Fluence</h1>
        <p className="cf-error">{gate.error}</p>
      </div>
    )
  }

  return (
    <div className="auth-screen">
      <h1>Fluence</h1>
      <p className="auth-screen__blurb">
        Will every account stay above zero over the next two years? Sign in to see your plan.
      </p>
      {gate.error && <p className="cf-error">{gate.error}</p>}
      <button type="button" className="btn-primary" onClick={() => void onSignIn()}>
        Sign in with Google
      </button>
    </div>
  )
}
